import { Display } from "rot-js"
import { EnemyType, PlayerAction } from "../defines/enums"
import { Beast, Offset } from "../defines/types"
import { EngineDefaults } from "../defines/values"
import { BLACK_TILE, BORDER_TILE, TapestryTile } from "../map/tile"
import { getBestiaryConfig } from "../utils/bestiary"
import { toDisplayString } from "../utils/color"
import { Popup } from "./popup"

export class Bestiary extends Popup {
  private _tiles: TapestryTile[][] = []

  private _beasts: Beast[] = []

  constructor(offsets: Offset, name: string) {
    super(
      EngineDefaults.TROPHY_CASE_WIDTH,
      EngineDefaults.TROPHY_CASE_HEIGHT,
      offsets,
      name
    )
    this.initTiles()
  }

  addBeast(type: EnemyType): boolean {
    const config = getBestiaryConfig(type)
    if (config === undefined) {
      return false
    }

    const beast: Beast = { ...config }
    if (beast === undefined) {
      return false
    }

    for (let i = 0; i < this._beasts.length; ++i) {
      if (beast.name == this._beasts[i].name) {
        return false
      }
    }

    this._beasts.push(beast)

    this._beasts.sort((b1, b2) => {
      if (b1.name.toUpperCase() < b2.name.toUpperCase()) {
        return -1
      } else if (b1.name.toUpperCase() == b2.name.toUpperCase()) {
        return 0
      }

      return 1
    })

    return true
  }

  seeBeast(type: EnemyType): void {
    for (let i = 0; i < this._beasts.length; ++i) {
      if (this._beasts[i].type == type) {
        this._beasts[i].seen = true
        return
      }
    }
  }

  killBeast(type: EnemyType): void {
    for (let i = 0; i < this._beasts.length; ++i) {
      if (this._beasts[i].type == type) {
        this._beasts[i].seen = true
        this._beasts[i].killCount += 1
        return
      }
    }
  }

  prepareToRender(): void {
    return
  }

  postRender(): void {}

  render(display: Display): void {
    for (let y = 0; y < this._tiles.length; ++y) {
      const row = this._tiles[y]
      for (let x = 0; x < row.length; ++x) {
        const tile = row[x]
        display.draw(
          x + this.offsets.xOffset,
          y + this.offsets.yOffset,
          tile.symbol,
          toDisplayString(tile.fg),
          toDisplayString(tile.bg)
        )
      }
    }

    let colors: string = "%c{#fff}%b{#000}"
    const title: string = "Bestiary"
    let printRow: number = 1
    let xStart: number = this.width / 2 - title.length / 2

    display.drawText(xStart, printRow, colors + title)

    let output: string = `A record of the ethereal creatures you have encountered.`

    printRow += 2
    xStart = this.width / 2 - output.length / 2
    display.drawText(xStart, printRow, colors + output)

    printRow += 1
    xStart = this.offsets.xOffset + 1
    display.drawText(
      xStart,
      printRow,
      colors + "-".padEnd(this.width - 2, "-") // 2 accounts for walls
    )

    let beastsKilled: number = 0
    this._beasts.forEach((b) => {
      beastsKilled += b.killCount
    })

    const tag: string = " vanquished]"
    const outputLength: number = beastsKilled + tag.length + 1 // leading [

    const counterColors: string = "%c{#0ff}%b{#000}"
    output =
      `%c{#fff}%b{#000}[` +
      `${counterColors}${beastsKilled}` +
      `%c{#fff}%b{#000}${tag}`

    printRow += 1
    xStart = this.width / 2 - outputLength / 2
    display.drawText(xStart, printRow, output)

    // NOTE: the values 3, 15, 35 below are manually counted against
    //       longest possible strings for these fields
    printRow += 3
    for (let i = 0; i < this._beasts.length; ++i) {
      const beast = this._beasts[i]
      if (beast.seen) {
        let output: string = `%c{${beast.color}}%b{#000}${beast.symbol.padEnd(
          3,
          " "
        )} `
        output += `%c{#fff}%b{#000}${beast.name.padEnd(15)}`
        if (beast.killCount > 0) {
          output += " -> " + beast.description.padEnd(35, " ")
          output +=
            " [x %c{#0ff}%b{#000}" + beast.killCount + "%c{#fff}%b{#000}]"
        } else {
          output += " -> %c{#ff0}%b{#000}seen"
        }
        display.drawText(3, printRow, output)
      } else {
        display.drawText(3, printRow, `%c{#fff}%b{#000}?`)
      }
      printRow += 1
    }

    // key
    printRow = this.height - 3
    xStart = 2
    display.drawText(xStart, printRow, "KEY")

    printRow += 1
    display.drawText(xStart, printRow, "  ? new enemy type on map, unseen")
  }

  update(action: PlayerAction): void {
    return
  }

  protected initTiles() {
    this._tiles = new Array(this.height)
    for (let y = 0; y < this.height; y++) {
      const isOddRow: boolean = y % 2 != 0 ? true : false
      const row = new Array(this.width)
      for (let x = 0; x < this.width; x++) {
        const isWall: boolean =
          x === 0 || x === this.width - 1 || y === 0 || y === this.height - 1
        row[x] = isWall ? { ...BORDER_TILE } : { ...BLACK_TILE }
      }
      this._tiles[y] = row
    }
  }
}
